/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Chaitanya
*/

import React, { useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { isWeb, fullWidth } from '@global/screens';
import Colors from '@global/colors';
import { hasNoAccess } from '@global/helpers';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import ActionButton from '@components/navbar/ActionButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import * as Layout from '@components/screen/Layout';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetCoaTaxListQuery,
  useGetCoaTaxCountQuery,
  useGetCoaTaxInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  cost_type: { head: 'Cost Type', body: 'cost_type' },
  cost_number: { head: 'COA Number', body: 'coa_number' },
  pph_npwp_badan: {
    head: 'Tax ID Corporate',
    body: 'pph_npwp_badan',
    type: 'percentage',
  },
  pph_npwp_pribadi: {
    head: 'Tax ID Personal',
    body: 'pph_npwp_pribadi',
    type: 'percentage',
  },
  ppn: { head: 'VAT', body: 'vat', type: 'percentage' },
  is_active: {
    head: 'Active/Inactive',
    body: 'is_active',
    type: 'status_active',
  },
};

// Search-only: the search box writes `keyword`; the ES endpoint wants a plain
// string, so unwrap the { $regex } wrapper. (Mongo↔ES switch is keyed on
// hasFilter in the resource — any keyword flips to the ES search endpoint.)
const toApiFilter = (raw = {}) => {
  const filter = {};
  const kw = raw.keyword?.$regex ?? raw.keyword;
  if (kw) {
    filter.keyword = kw;
  }
  return filter;
};

// Display row (RTK cache frozen → new object): flatten the default tax values.
const mapRow = (r) => {
  const next = { ...r, id: r._id };
  const defaultTaxCorporate = (r.tax_corporate || []).find((t) => t.is_default);
  if (defaultTaxCorporate) {
    next.pph_npwp_badan = defaultTaxCorporate.tax_value;
  }
  const defaultTaxPersonal = (r.tax_personal || []).find((t) => t.is_default);
  if (defaultTaxPersonal) {
    next.pph_npwp_pribadi = defaultTaxPersonal.tax_value;
  }
  const defaultVat = (r.vat || []).find((t) => t.is_default);
  if (defaultVat) {
    next.vat = defaultVat.tax_value;
  }
  return next;
};

const CoaTaxSettingScreen = (props) => {
  // ── derived ──
  const showAddButton = !hasNoAccess([
    'superadmin',
    'finance_head',
    'tax_support',
    'general',
  ]);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetCoaTaxListQuery,
    countHook: useGetCoaTaxCountQuery,
    infiniteHook: useGetCoaTaxInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  // ── handlers ──
  const goToDetail = ({ data }) => {
    openChild('CoaTaxSettingDetailScreen', {
      id: data._id,
      titleScreen: data.cost_type,
      cost_type: data.cost_type,
    });
  };

  const goToForm = () => {
    openChild('CoaTaxSettingFormScreen');
  };

  // ── effects ──
  // Own the header so the mobile Add goes through openChild (preserves the list
  // on return) rather than the navigator's plain navigate.
  useEffect(() => {
    props.navigation.setOptions({
      headerRight: () =>
        isWeb ? (
          <NotificationSettingButton {...props} />
        ) : showAddButton ? (
          <ActionButton
            icon={require('@assets/img/icons/plus-white.svg')}
            onCallback={goToForm}
          />
        ) : (
          false
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, showAddButton]);

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="COA Tax Setting">
            {showAddButton ? (
              <View style={{ flexDirection: 'row' }}>
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 100 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      + Add
                    </SociollaText>
                  }
                  onPress={() => goToForm()}
                />
              </View>
            ) : (
              false
            )}
          </Layout.Title>

          <Table
            filterName={scope}
            searchKey="keyword"
            searchPlaceholder="Search by cost type"
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            hasCallback={true}
            showFilter={false}
            onClickRow={(val) => goToDetail(val)}
            searchValue={searchValue}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="keyword"
          placeholder="Search by cost type"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          searchValue={searchValue}
          renderItem={({ data, index }) => (
            <Layout.ItemMobile
              key={index}
              touchable={true}
              column={true}
              onPress={() => goToDetail({ data: data })}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}>
                <View style={{ width: fullWidth - 140 }}>
                  <SociollaText
                    type="medium"
                    style={{ width: '100%', marginBottom: 4 }}>
                    {data.cost_type}
                  </SociollaText>
                  <SociollaText
                    type="medium"
                    style={{ width: '100%', marginBottom: 4 }}>
                    {data.coa_number}
                  </SociollaText>
                  <SociollaText
                    type="medium"
                    style={{ width: '100%', marginBottom: 4 }}>
                    {data.pph_npwp}
                  </SociollaText>
                  <SociollaText
                    type="medium"
                    style={{ width: '100%', marginBottom: 4 }}>
                    {data.pph_non_npwp}
                  </SociollaText>
                </View>
              </View>
            </Layout.ItemMobile>
          )}
        />
      )}
    </Layout.Container>
  );
};

export default CoaTaxSettingScreen;
